import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { FileText, Link2, Check, X, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Actions as IMPORT_ACTIONS } from '../../store/actions/intelligentImportActions';

const SingleDocumentReview = ({
  importId,
  job,
  stagingItems,
  enrichmentCandidates,
  getStagingItems,
  getEnrichmentCandidates,
  linkEnrichment,
  confirmMatch,
  getFactorMatches,
  factorMatches,
  loading,
}) => {
  const [selectedLink, setSelectedLink] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (importId) {
      getStagingItems(importId);
      getEnrichmentCandidates(importId);
      getFactorMatches(importId);
    }
  }, [importId]);

  const docRows = useMemo(() =>
    stagingItems.filter((r) => r.dataKind === 'DOCUMENT' || r.isDocument),
  [stagingItems]);

  const handleLink = useCallback(async (docId, spendId) => {
    setLinking(true);
    try {
      await linkEnrichment(importId, docId, spendId);
      await getStagingItems(importId, { silent: true });
      await getEnrichmentCandidates(importId);
    } finally {
      setLinking(false);
    }
  }, [importId, linkEnrichment, getStagingItems, getEnrichmentCandidates]);

  if (loading?.staging) {
    return <div className='text-center py-12 text-muted-foreground'>Loading document data...</div>;
  }

  return (
    <div className='flex flex-col gap-4'>
      <Card className='bg-card'>
        <CardHeader>
          <CardTitle className='text-lg flex items-center gap-2'>
            <FileText size={20} strokeWidth={1.5} />
            Extracted Document Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          {docRows.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No document data extracted.</p>
          ) : (
            <div className='flex flex-col gap-4'>
              {docRows.map((doc) => {
                const matches = (factorMatches || []).filter(
                  (m) => m.stagingId === (doc.id || doc.stagingId),
                );
                return (
                  <Card key={doc.id || doc.stagingId} className='bg-muted/30'>
                    <CardContent className='p-4'>
                      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                        <div>
                          <p className='text-xs text-muted-foreground'>Description</p>
                          <p className='text-sm font-medium'>{doc.description || '—'}</p>
                        </div>
                        <div>
                          <p className='text-xs text-muted-foreground'>Amount</p>
                          <p className='text-sm font-mono'>{doc.currency || '£'}{Number(doc.amount || 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className='text-xs text-muted-foreground'>Date</p>
                          <p className='text-sm'>{doc.date || '—'}</p>
                        </div>
                        <div>
                          <p className='text-xs text-muted-foreground'>Supplier</p>
                          <p className='text-sm'>{doc.supplierName || doc.vendor || '—'}</p>
                        </div>
                      </div>
                      {doc.lineItems?.length > 0 && (
                        <div className='mt-3'>
                          <p className='text-xs text-muted-foreground mb-1'>Line Items</p>
                          <Table>
                            <TableBody>
                              {doc.lineItems.map((li, i) => (
                                <TableRow key={i}>
                                  <TableCell className='text-sm'>{li.description}</TableCell>
                                  <TableCell className='text-right text-sm font-mono'>
                                    {doc.currency || '£'}{Number(li.amount || 0).toFixed(2)}
                                  </TableCell>
                                  {li.quantity && (
                                    <TableCell className='text-sm text-muted-foreground'>
                                      {li.quantity} {li.unit || ''}
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                      {matches.length > 0 && (
                        <div className='mt-3'>
                          <p className='text-xs text-muted-foreground mb-1'>Factor Match</p>
                          <div className='flex items-center gap-3'>
                            <span className='text-sm'>{matches[0].factorName}</span>
                            <Badge className='bg-info-bg text-info'>
                              {Math.round((matches[0].confidence || 0) * 100)}% match
                            </Badge>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='text-success'
                              onClick={() => confirmMatch(importId, matches[0].id || matches[0].matchId, doc.id || doc.stagingId)}
                            >
                              <Check size={14} strokeWidth={1.5} /> Confirm
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {enrichmentCandidates.length > 0 && (
        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-lg flex items-center gap-2'>
              <Link2 size={20} strokeWidth={1.5} />
              Link to Existing Spend Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className='text-sm text-muted-foreground mb-3'>
              These existing spend records match by date and amount. Link this document to upgrade their quality.
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className='text-right'>Amount</TableHead>
                  <TableHead>Match Score</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrichmentCandidates.map((cand) => (
                  <TableRow key={cand.id || cand.spendId}>
                    <TableCell className='text-sm'>{cand.date || '—'}</TableCell>
                    <TableCell className='text-sm'>{cand.description}</TableCell>
                    <TableCell className='text-right font-mono text-sm'>
                      {cand.currency || '£'}{Number(cand.amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge className='bg-info-bg text-info'>
                        {Math.round((cand.matchScore || cand.similarity || 0) * 100)}%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant='outline'
                        size='sm'
                        loading={linking}
                        onClick={() => handleLink(docRows[0]?.id || docRows[0]?.stagingId, cand.id || cand.spendId)}
                      >
                        <Link2 size={14} strokeWidth={1.5} />
                        Link
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  stagingItems: state.intelligentImport?.stagingItems || [],
  enrichmentCandidates: state.intelligentImport?.enrichmentCandidates || [],
  factorMatches: state.intelligentImport?.factorMatches || [],
  loading: state.intelligentImport?.loading || {},
});

const mapDispatchToProps = {
  getStagingItems: IMPORT_ACTIONS.getStagingItems,
  getEnrichmentCandidates: IMPORT_ACTIONS.getEnrichmentCandidates,
  linkEnrichment: IMPORT_ACTIONS.linkEnrichment,
  confirmMatch: IMPORT_ACTIONS.confirmMatch,
  getFactorMatches: IMPORT_ACTIONS.getFactorMatches,
};

export default connect(mapStateToProps, mapDispatchToProps)(SingleDocumentReview);
